import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Box, Divider, IconButton, Typography } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import { AddCircleOutline as AddIcon, CloseOutlined } from '@mui/icons-material';

import { FHButton, FHInputField } from '../../../base';
import { clientFamilyBackgroundActions } from '../../../../store';
import { useTreatmentPlanEditable } from '../hooks/useTreatmentPlanEditable';
import { FORTA_BLUE, FORTA_WHITE } from '../../../../utils/colors';
import { Themes } from '../../../../constants';

import type { RootState } from '../../../../store';

function Interests() {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const isEditable = useTreatmentPlanEditable();
  const { current: treatmentPlan } = useSelector((state: RootState) => state.treatmentPlans);
  const { interests = [] } = useSelector((state: RootState) => state.clientFamilyBackgrounds);

  const [newInterest, setNewInterest] = useState('');

  useEffect(() => {
    const isError = isEditable && interests?.length === 0;
    dispatch(
      clientFamilyBackgroundActions.updateState({
        key: 'interestErrorCount',
        data: isError ? 1 : 0,
      }),
    );
  }, [isEditable, interests]);

  const handleAddInterest = async () => {
    if (newInterest.length < 2) {
      toast.error('Interest must be at least 2 characters');
      return;
    }
    if (interests?.some((interest) => interest.toLowerCase() === newInterest.toLowerCase())) {
      toast.error('Interest already exists');
      return;
    }

    try {
      const updatedInterests = [...(interests || []), newInterest];
      const res = await dispatch(
        clientFamilyBackgroundActions.updateInterests({
          treatmentPlanId: treatmentPlan?.id,
          data: {
            client_interests: updatedInterests,
          },
          getAccessTokenSilently,
        }),
      ).unwrap();

      if (!res.error) {
        setNewInterest('');
        toast.success('Interest added successfully');
      }
    } catch {
      toast.error('Failed to add interest');
    }
  };

  const handleDeleteInterest = async (index: number) => {
    if (!interests) return;
    try {
      const updatedInterests = interests.filter((_, i) => i !== index);

      const res = await dispatch(
        clientFamilyBackgroundActions.updateInterests({
          treatmentPlanId: treatmentPlan?.id,
          data: {
            client_interests: updatedInterests,
          },
          getAccessTokenSilently,
        }),
      ).unwrap();

      if (!res.error) {
        toast.success('Interest deleted successfully');
      }
    } catch {
      toast.error('Failed to delete interest');
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <Typography fontSize={18} fontWeight="bold" textAlign="left">
        Interests/Dislikes*
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '16px' }}>
        <FHInputField
          data-testid="interestsTextField"
          value={newInterest}
          sx={{ width: '250px' }}
          onChange={(e) => setNewInterest(e.target.value)}
          disabled={!isEditable}
          fullWidth
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleAddInterest();
            }
          }}
        />
        <FHButton
          data-testid="interestsAddButton"
          colorTheme={Themes.Primary}
          startIcon={<AddIcon sx={{ color: FORTA_WHITE }} />}
          sx={{ width: 'fit-content' }}
          disabled={!isEditable}
          onClick={handleAddInterest}
          error={interests?.length === 0}
        >
          ADD
        </FHButton>
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
        {interests &&
          interests.length > 0 &&
          interests.map((interest, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                backgroundColor: 'white',
                borderRadius: '8px',
                padding: '6px',
              }}
            >
              <Typography pl={1} fontSize={16}>
                {interest}
              </Typography>
              <Divider
                orientation="vertical"
                flexItem
                sx={{ ml: 2, my: 0.1, borderColor: FORTA_BLUE.light1 }}
              />
              {isEditable && (
                <IconButton
                  data-testid="interestsDeleteButton"
                  onClick={() => handleDeleteInterest(index)}
                  sx={{
                    padding: '4px',
                    '&:hover': { opacity: 0.8 },
                  }}
                >
                  <CloseOutlined sx={{ color: FORTA_BLUE.primary }} />
                </IconButton>
              )}
            </Box>
          ))}
      </Box>
    </Box>
  );
}

export default Interests;
